import { CustomSelect } from "@/components/ui/Select";
import type { TemplateTask } from "@/types/task";

interface TaskFormBasicFieldsProps {
  formData: {
    title?: string;
    description?: string;
    layer_name?: string;
  };
  selectedTemplate: string;
  errors: Record<string, string>;
  isEditing: boolean;
  isViewMode: boolean;
  onTitleChange: (value: string) => void;
  onDescriptionChange: (value: string) => void;
  onTemplateChange: (templateId: string) => void;
  getTemplatesByLayer: (layerName: string) => TemplateTask[];
}

export const TaskFormBasicFields: React.FC<TaskFormBasicFieldsProps> = ({
  formData,
  selectedTemplate,
  errors,
  isEditing,
  isViewMode,
  onTitleChange,
  onDescriptionChange,
  onTemplateChange,
  getTemplatesByLayer,
}) => {
  // Get available templates for the current layer
  const availableTemplates = formData.layer_name
    ? getTemplatesByLayer(formData.layer_name)
    : getTemplatesByLayer("no_layer");

  return (
    <>
      <div className="box-border content-stretch flex flex-row gap-4 items-start justify-start p-0 relative shrink-0 w-full">
        {/* Title */}
        <div className="basis-0 box-border content-stretch flex flex-col gap-1.5 grow items-start justify-start min-h-px min-w-px p-0 relative shrink-0">
          <div className="box-border content-stretch flex flex-row gap-2 items-center justify-start p-0 relative shrink-0">
            <div
              className="font-['Open_Sans:SemiBold',sans-serif] font-semibold leading-0 relative shrink-0 text-[#444444] text-[14px] text-left text-nowrap"
              style={{ fontVariationSettings: "'wdth' 100" }}
            >
              <p className="leading-normal whitespace-pre">
                <span>Title </span>
                <span className="text-[#e7000b]">*</span>
              </p>
            </div>
          </div>
          <div className="bg-gray-100 relative rounded-[10px] shrink-0 w-full">
            <div className="flex flex-row items-center relative size-full">
              <div className="box-border content-stretch flex flex-row gap-4 items-center justify-start px-3.5 py-[13px] relative w-full">
                <input
                  type="text"
                  value={formData.title || ""}
                  onChange={(e) => onTitleChange(e.target.value)}
                  className={`w-full bg-transparent border-none outline-none font-['Open_Sans:Medium',sans-serif] leading-0 not-italic text-[12px] text-left ${
                    formData.title ? "text-gray-900" : "text-[#828282]"
                  } ${errors.title ? "text-red-500" : ""}`}
                  placeholder="Type Here"
                  autoFocus
                  disabled={isViewMode}
                />
              </div>
            </div>
          </div>
          {errors.title && (
            <p className="mt-1 text-xs text-red-600">{errors.title}</p>
          )}
        </div>

        {/* Apply Template */}
        {!isEditing && (
          <div className="basis-0 box-border content-stretch flex flex-col gap-1.5 grow items-start justify-start min-h-px min-w-px p-0 relative shrink-0">
            <div className="box-border content-stretch flex flex-row gap-2 items-center justify-start p-0 relative shrink-0">
              <div
                className="font-['Open_Sans:SemiBold',sans-serif] font-semibold leading-0 relative shrink-0 text-[#444444] text-[14px] text-left text-nowrap"
                style={{ fontVariationSettings: "'wdth' 100" }}
              >
                <p className="leading-normal whitespace-pre">
                  <span>Apply Template </span>
                  <span
                    className="font-['Open_Sans:SemiBold_Italic',sans-serif] font-semibold italic text-[#828282]"
                    style={{ fontVariationSettings: "'wdth' 100" }}
                  >
                    (Optional)
                  </span>
                </p>
              </div>
            </div>
            <CustomSelect
              value={selectedTemplate}
              onValueChange={onTemplateChange}
              options={[
                ...(availableTemplates.length > 0
                  ? [{ value: "clear", label: "Clear Template" }]
                  : []),
                ...availableTemplates.map((template) => ({
                  value: template.id,
                  label: template.name,
                })),
              ]}
              disabled={isViewMode}
              placeholder="Select Template"
            />
          </div>
        )}
      </div>

      {/* Description */}
      <div className="box-border content-stretch flex flex-col gap-1.5 items-start justify-start p-0 relative shrink-0 w-full">
        <div className="box-border content-stretch flex flex-row gap-2 items-center justify-start p-0 relative shrink-0">
          <div
            className="font-['Open_Sans:SemiBold',sans-serif] font-semibold leading-0 relative shrink-0 text-[#444444] text-[14px] text-left text-nowrap"
            style={{ fontVariationSettings: "'wdth' 100" }}
          >
            <p className="block leading-normal whitespace-pre">Descriptions</p>
          </div>
        </div>
        <div className="bg-gray-100 relative rounded-[10px] shrink-0 w-full">
          <textarea
            value={formData.description || ""}
            onChange={(e) => onDescriptionChange(e.target.value)}
            rows={3}
            className={`w-full bg-transparent border-none outline-none resize-none font-['Open_Sans:Medium',sans-serif] leading-normal not-italic text-[12px] text-left px-3.5 py-[13px] ${
              formData.description ? "text-gray-900" : "text-[#828282]"
            }`}
            disabled={isViewMode}
            placeholder="Type Here"
          />
        </div>
      </div>
    </>
  );
};
